app.service("ChunkedRecordingService", [
    "$q", "ENV", "Session", "$timeout", "TranscriptionAPI",
    function ($q, ENV, Session, $timeout, TranscriptionAPI) {
        const service = this;

        service.isRecording = false;
        service.isConnecting = false;
        service.transcriptionId = null;
        service.rawText = "";
        service.micSilenceWarning = false;

        let audioContext;
        let mediaStream;
        let workletNode;
        // The practice the running session records under, fixed when it starts so a chunk queued
        // or retried later is not sent under whichever practice is selected by then.
        let sessionPracticeId;
        // Bumped whenever a start is abandoned (stopped while still connecting), so a start that
        // finishes afterwards releases the microphone instead of recording with no UI to stop it.
        let startToken = 0;

        // Queue state
        let chunkQueue = [];
        let isUploadingQueue = false;
        let lastProcessedTranscriptionId = null;

        // Chunking state
        let pcmBuffer = [];
        let bufferLength = 0;
        let chunkStartTime = 0;
        let chunkSequenceIndex = 0;
        let currentUploadPromise = null;
        let deadMicConsecutiveChunks = 0;

        // Constants for chunking & silence detection
        const SAMPLE_RATE = 16000;
        const MIN_CHUNK_DURATION_MS = 25000;
        const MAX_CHUNK_DURATION_MS = 35000;
        const SILENCE_THRESHOLD = 500;

        service.startSession = async function (practiceId, patientId, transcriptionId, deviceId, startingSequenceIndex, onError) {
            const myToken = ++startToken;
            const isAbandoned = () => myToken !== startToken;
            // Held locally until the start is known to still be wanted, so an abandoned start only
            // ever releases its own microphone and never one a newer start has opened since.
            let ownStream = null;
            let ownContext = null;
            const abandon = () => {
                if (ownStream) ownStream.getTracks().forEach(track => track.stop());
                if (ownContext) ownContext.close();
                if (mediaStream === ownStream) mediaStream = null;
                if (audioContext === ownContext) audioContext = null;
            };
            try {
                service.isConnecting = true;
                sessionPracticeId = practiceId || (Session.practice ? Session.practice.id : null);

                $timeout(() => { service.micSilenceWarning = false; });

                // If no transcription ID was passed, create one first
                service.transcriptionId = transcriptionId;
                if (!service.transcriptionId) {
                    const createResp = await TranscriptionAPI.createTranscription(practiceId, patientId);
                    // Abandoned while creating: a newer start may own transcriptionId by now.
                    if (isAbandoned()) {
                        return;
                    }
                    service.transcriptionId = createResp.data.transcription_id;
                }

                // Get microphone access
                const constraints = {
                    audio: deviceId ? { deviceId: { exact: deviceId } } : true
                };
                ownStream = await navigator.mediaDevices.getUserMedia(constraints);
                if (isAbandoned()) {
                    abandon();
                    return;
                }
                mediaStream = ownStream;

                ownContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: SAMPLE_RATE });
                audioContext = ownContext;

                await audioContext.audioWorklet.addModule('app/src/js/components/transcription/pcmProcessor.js');
                if (isAbandoned()) {
                    abandon();
                    return;
                }

                const source = audioContext.createMediaStreamSource(mediaStream);
                workletNode = new AudioWorkletNode(audioContext, 'pcm-processor');

                // Reset state for the new recording session
                pcmBuffer = [];
                bufferLength = 0;
                chunkStartTime = Date.now();
                currentUploadPromise = Promise.resolve();
                deadMicConsecutiveChunks = 0;

                // Set to the provided index, or default to 0 if starting fresh
                chunkSequenceIndex = startingSequenceIndex || 0;

                // Listen for PCM data from the worklet
                workletNode.port.onmessage = handleAudioData;

                source.connect(workletNode);
                workletNode.connect(audioContext.destination);

                $timeout(() => {
                    if (isAbandoned()) {
                        abandon();
                        return;
                    }
                    service.isConnecting = false;
                    service.isRecording = true;
                });

            } catch (err) {
                console.error("Error starting chunked session:", err && (err.status || err.name));
                // The microphone may already be open if a later step failed.
                abandon();
                service.isConnecting = false;
                $timeout(() => { service.isConnecting = false; });
                if (onError) onError(err);
            }
        };

        service.stopSession = function () {
            if (!service.isRecording) {
                if (service.isConnecting) {
                    // Still starting: abandon that start, so it lets go of the mic when it lands.
                    startToken++;
                    $timeout(() => { service.isConnecting = false; });
                }
                return Promise.resolve();
            }

            const deferred = $q.defer();

            if (service.micSilenceWarning) {
                $timeout(() => {
                    service.micSilenceWarning = false;
                });
            }

            if (workletNode) {
                workletNode.port.postMessage('flush');
            }

            // Only this session's id is cleared once its last chunk lands - a new recording may have
            // started by then, and nulling its id would silently drop all of its audio.
            const stoppedTranscriptionId = service.transcriptionId;
            const clearStoppedId = () => {
                if (service.transcriptionId === stoppedTranscriptionId) {
                    service.transcriptionId = null;
                }
            };

            setTimeout(() => {
                const finalUploadPromise = flushBufferToAPI(true);

                if (mediaStream) {
                    mediaStream.getTracks().forEach(track => track.stop());
                }
                if (audioContext) {
                    audioContext.close();
                }

                service.isRecording = false;

                if (finalUploadPromise) {
                    finalUploadPromise.then(() => {
                        clearStoppedId();
                        deferred.resolve();
                    }).catch((err) => {
                        clearStoppedId();
                        deferred.reject(err);
                    });
                } else {
                    clearStoppedId();
                    deferred.resolve();
                }
            }, 150);

            return deferred.promise;
        };

        function handleAudioData(event) {
            if (!service.isRecording) return;

            const pcmData = new Int16Array(event.data);
            pcmBuffer.push(pcmData);
            bufferLength += pcmData.length;

            const now = Date.now();
            const elapsedMs = (bufferLength / SAMPLE_RATE) * 1000;

            let sum = 0;
            for (let i = 0; i < pcmData.length; i++) {
                sum += Math.abs(pcmData[i]);
            }
            const averageAmplitude = sum / pcmData.length;

            // If amplitude is exactly 0 (or close to it)
            if (averageAmplitude < 1) {
                deadMicConsecutiveChunks++;
                // 40 chunks = roughly 10 seconds of pure silence
                if (deadMicConsecutiveChunks > 40) {
                    console.warn("Microphone is capturing absolute silence. Please check OS privacy settings or hardware mute.");

                    // Trigger the popover and notify Angular
                    if (!service.micSilenceWarning) {
                        $timeout(() => {
                            service.micSilenceWarning = true;
                        });
                    }

                    deadMicConsecutiveChunks = 0; // reset to avoid spamming 
                }
            } else {
                deadMicConsecutiveChunks = 0;

                // Auto-dismiss the popover if sound naturally returns
                if (service.micSilenceWarning) {
                    $timeout(() => {
                        service.micSilenceWarning = false;
                    });
                }
            }

            const isNaturalPause = elapsedMs >= MIN_CHUNK_DURATION_MS && averageAmplitude < SILENCE_THRESHOLD;
            const isForcedCut = elapsedMs >= MAX_CHUNK_DURATION_MS;

            if (isNaturalPause || isForcedCut) {
                flushBufferToAPI();
            }
        }

        function flushBufferToAPI(isLastChunk = false) {
            if (bufferLength === 0 && !isLastChunk) return Promise.resolve();
            if (!service.transcriptionId) return Promise.resolve();

            const currentTId = service.transcriptionId;

            const flatBuffer = new Int16Array(bufferLength);
            let offset = 0;
            for (let i = 0; i < pcmBuffer.length; i++) {
                flatBuffer.set(pcmBuffer[i], offset);
                offset += pcmBuffer[i].length;
            }

            pcmBuffer = [];
            bufferLength = 0;
            chunkStartTime = Date.now();

            const wavBlob = encodeWAV(flatBuffer, SAMPLE_RATE);
            const currentIndex = chunkSequenceIndex++;

            // Return a promise that resolves only when THIS specific chunk succeeds.
            // This allows stopSession() to safely wait for the queue to drain.
            return new Promise((resolve, reject) => {
                chunkQueue.push({
                    blob: wavBlob,
                    sequenceIndex: currentIndex,
                    isLastChunk: isLastChunk,
                    transcriptionId: currentTId,
                    practiceId: sessionPracticeId,
                    resolve: resolve,
                    reject: reject
                });

                processQueue();
            });
        }

        function processQueue() {
            // If we are currently uploading, or the queue is empty, do nothing.
            if (isUploadingQueue || chunkQueue.length === 0) return;

            isUploadingQueue = true;

            // --- TRUE ROUND-ROBIN SELECTION LOGIC ---
            // Get an array of all unique transcription IDs currently in the queue
            const uniqueIds = [...new Set(chunkQueue.map(c => c.transcriptionId))];

            // Default to the first available ID
            let nextId = uniqueIds[0];

            // If we remember the last uploaded ID, find its index in our unique list
            if (lastProcessedTranscriptionId) {
                const lastIndex = uniqueIds.indexOf(lastProcessedTranscriptionId);

                // If it exists, use modulo math to grab the next one in the circle.
                // If lastIndex is the end of the array, this loops back to 0.
                if (lastIndex !== -1) {
                    nextId = uniqueIds[(lastIndex + 1) % uniqueIds.length];
                }
            }

            // Find the absolute oldest chunk that belongs to our chosen ID
            const targetIndex = chunkQueue.findIndex(c => c.transcriptionId === nextId);
            const currentChunk = chunkQueue[targetIndex];
            // ----------------------------------------

            const formData = new FormData();
            formData.append('file', currentChunk.blob, `chunk_${Date.now()}.wav`);

            TranscriptionAPI.uploadTranscriptionChunk(
                currentChunk.practiceId,
                currentChunk.transcriptionId,
                currentChunk.sequenceIndex,
                formData,
                currentChunk.isLastChunk
            ).then(() => {
                // Success! Update the tracker so we rotate away from this ID next time
                lastProcessedTranscriptionId = currentChunk.transcriptionId;

                // Remove THIS SPECIFIC chunk from the queue
                chunkQueue.splice(targetIndex, 1);

                // Resolve the promise tied to this chunk
                if (currentChunk.resolve) currentChunk.resolve();

                isUploadingQueue = false;

                // Immediately try to process the next item in the queue
                processQueue();

            }).catch((err) => {
                const status = err && err.status;
                isUploadingQueue = false;

                // A client error will fail the same way every time (timeouts and rate limits
                // aside), so the chunk is given up on rather than retried forever - which also left
                // stopSession() waiting on it for good.
                if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
                    console.warn(`[Network] Chunk ${currentChunk.sequenceIndex} was rejected (${status}); not retrying.`);
                    const index = chunkQueue.indexOf(currentChunk);
                    if (index !== -1) chunkQueue.splice(index, 1);
                    if (currentChunk.reject) currentChunk.reject(err);
                    processQueue();
                    return;
                }

                console.warn(`[Network] Chunk ${currentChunk.sequenceIndex} failed to upload (${status}). Retrying in 3 seconds...`);

                // Wait 3 seconds before retrying to avoid spamming a dead connection
                setTimeout(() => {
                    processQueue();
                }, 3000);
            });
        }

        // --- TAB CLOSE / REFRESH ---
        // Audio still in the buffer or the upload queue is lost when the page goes, so ask first while
        // there is any. The browser shows its own "Leave site?" prompt; staying keeps everything.
        window.addEventListener('beforeunload', function (event) {
            if (service.isRecording || chunkQueue.length > 0) {
                event.preventDefault();
                event.returnValue = ''; // Older browsers only prompt with this set
            }
        });

        // Once the page is really going, end every transcription that would otherwise never get its final
        // chunk: the one still recording, and a stopped one whose final chunk is still waiting to upload.
        // Without that signal the transcription is never finished. Sent from pagehide rather than
        // beforeunload, so choosing to stay on the page does not end the recording.
        window.addEventListener('pagehide', function () {
            const closingChunks = {};
            if (service.isRecording && service.transcriptionId) {
                closingChunks[service.transcriptionId] = { sequenceIndex: chunkSequenceIndex, practiceId: sessionPracticeId };
            }
            chunkQueue.forEach(chunk => {
                const closingChunk = closingChunks[chunk.transcriptionId];
                if (!closingChunk || closingChunk.sequenceIndex <= chunk.sequenceIndex) {
                    closingChunks[chunk.transcriptionId] = { sequenceIndex: chunk.sequenceIndex + 1, practiceId: chunk.practiceId };
                }
            });

            Object.keys(closingChunks).forEach(transcriptionId => {
                sendClosingChunk(transcriptionId, closingChunks[transcriptionId]);
            });
        });

        function sendClosingChunk(transcriptionId, closingChunk) {
            // Create a 0-length buffer. This generates a 44-byte WAV file (headers only).
            // This guarantees we stay well under the strict 64KB fetch keepalive limit,
            // prioritizing the "isLastChunk=true" signal over the final seconds of audio data.
            const wavBlob = encodeWAV(new Int16Array(0), SAMPLE_RATE);
            const formData = new FormData();
            formData.append('file', wavBlob, `chunk_closing_${Date.now()}.wav`);

            // Create the exact URL used by the TranscriptionAPI, with query params. The index follows every
            // chunk already numbered, so it never collides with one still in flight; the API also accepts
            // a chunk that lands after this one.
            const baseUrl = ENV.API ? ENV.API : "";
            const apiUrl = `${baseUrl}/api/transcription/${transcriptionId}?sequenceIndex=${closingChunk.sequenceIndex}&isLastChunk=true`;

            // Fire a raw fetch request using keepalive
            fetch(apiUrl, {
                method: 'PATCH',
                body: formData,
                keepalive: true, // Request continues after tab closes
                headers: {
                    'X-PRACTICE-ID': `${closingChunk.practiceId || (Session.practice ? Session.practice.id : "")}`,
                    'Authorization': "Bearer " + Session.token
                }
            }).catch(err => console.error("Emergency closing signal failed:", err && err.name));
        }

        // --- WAV Encoding Helper ---
        function encodeWAV(samples, sampleRate) {
            const buffer = new ArrayBuffer(44 + samples.length * 2);
            const view = new DataView(buffer);

            const writeString = function (view, offset, string) {
                for (let i = 0; i < string.length; i++) {
                    view.setUint8(offset + i, string.charCodeAt(i));
                }
            };

            writeString(view, 0, 'RIFF');
            view.setUint32(4, 36 + samples.length * 2, true);
            writeString(view, 8, 'WAVE');
            writeString(view, 12, 'fmt ');
            view.setUint32(16, 16, true);
            view.setUint16(20, 1, true);
            view.setUint16(22, 1, true);
            view.setUint32(24, sampleRate, true);
            view.setUint32(28, sampleRate * 2, true);
            view.setUint16(32, 2, true);
            view.setUint16(34, 16, true);
            writeString(view, 36, 'data');
            view.setUint32(40, samples.length * 2, true);

            let offset = 44;
            for (let i = 0; i < samples.length; i++, offset += 2) {
                view.setInt16(offset, samples[i], true);
            }

            return new Blob([view], { type: 'audio/wav' });
        }
    }
]);